import { useState, useEffect } from "react";
import { getContinuityReport, generateContinuityReport } from "@/services/sceneService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2, ShieldCheck, AlertTriangle, AlertCircle, Info,
  RefreshCw, CheckCircle2, Shirt, Armchair, Lightbulb, Palette, Building, Users
} from "lucide-react";


const SEVERITY_CONFIG = {
  CRITICAL: { icon: AlertCircle, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", label: "Critical" },
  WARNING: { icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", label: "Warning" },
  NOTE: { icon: Info, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30", label: "Note" },
};

const CATEGORY_ICONS = {
  costume: Shirt,
  props: Armchair,
  lighting: Lightbulb,
  color: Palette,
  set: Building,
  blocking: Users,
};

function ScoreGauge({ score }) {
  const color = score >= 80 ? "text-green-400" : score >= 60 ? "text-amber-400" : "text-red-400";
  const bg = score >= 80 ? "bg-green-500/10" : score >= 60 ? "bg-amber-500/10" : "bg-red-500/10";
  const border = score >= 80 ? "border-green-500/30" : score >= 60 ? "border-amber-500/30" : "border-red-500/30";

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl ${bg} border ${border}`}>
      <div className={`text-2xl font-bold ${color}`} data-testid="continuity-score">{score}</div>
      <div className="text-[10px] text-[var(--text-muted)] leading-tight">
        <div className={`font-medium ${color}`}>{score >= 80 ? "Good" : score >= 60 ? "Fair" : "Needs Work"}</div>
        <div>/ 100</div>
      </div>
    </div>
  );
}

export default function ContinuityReport({ scene, projectId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadReport();
  }, [projectId, scene.number]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await getContinuityReport(projectId, scene.number);
      setReport(data);
    } catch { setReport(null); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const data = await generateContinuityReport({
        project_id: projectId,
        scene_number: scene.number,
      });
      setReport(data);
      toast.success("Continuity report generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Analysis failed");
    } finally { setGenerating(false); }
  };

  if (loading) {
    return <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-[var(--color-gold-400)]" size={24} /></div>;
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-5 max-w-3xl" data-testid="continuity-report">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[rgba(212,168,83,0.1)] flex items-center justify-center">
              <ShieldCheck size={20} className="text-[var(--color-gold-400)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">Continuity Report</h3>
              <p className="text-[10px] text-[var(--text-muted)]">Visual consistency check across all shots in this scene</p>
            </div>
          </div>
          <Button onClick={handleGenerate} disabled={generating}
            className="btn-primary text-xs h-8" size="sm" data-testid="run-continuity-check-btn">
            {generating ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
            {generating ? "Analyzing..." : report ? "Re-run Check" : "Run Check"}
          </Button>
        </div>

        {/* Generating overlay */}
        {generating && (
          <div className="py-12 text-center">
            <Loader2 size={32} className="animate-spin text-[var(--color-gold-400)] mx-auto mb-3" />
            <p className="text-sm text-[var(--text-primary)] font-medium">Analyzing shots for continuity...</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">Checking costumes, props, lighting, color palette</p>
          </div>
        )}

        {/* No report yet */}
        {!report && !generating && (
          <div className="text-center py-16 bg-[var(--bg-overlay)] rounded-2xl border border-[var(--border-subtle)]">
            <ShieldCheck size={36} className="mx-auto text-[var(--text-disabled)] mb-3" />
            <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">No report yet</h4>
            <p className="text-xs text-[var(--text-muted)] max-w-xs mx-auto">
              Generate at least 2 shots in this scene, then run the continuity check to find inconsistencies.
            </p>
          </div>
        )}

        {/* Report content */}
        {report && !generating && (
          <div className="space-y-5">
            {/* Score + Summary */}
            <div className="p-4 bg-[var(--bg-overlay)] rounded-xl border border-[var(--border-subtle)]">
              <div className="flex items-start gap-4">
                <ScoreGauge score={report.overall_score || 0} />
                <div className="flex-1">
                  <p className="text-sm text-[var(--text-primary)] font-medium mb-1">{report.summary}</p>
                  <p className="text-[10px] text-[var(--text-disabled)]">
                    {report.total_shots_analyzed || 0} shots analyzed
                    {report.generated_at && ` — ${new Date(report.generated_at).toLocaleString()}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Issues */}
            {report.issues?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                  <AlertTriangle size={13} className="text-amber-400" />
                  Issues Found ({report.issues.length})
                </h4>
                <div className="space-y-2">
                  {report.issues.map((issue, i) => {
                    const sev = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.NOTE;
                    const SevIcon = sev.icon;
                    const CatIcon = CATEGORY_ICONS[issue.category] || Info;
                    return (
                      <div key={i} className={`p-3 rounded-xl ${sev.bg} border ${sev.border}`} data-testid={`issue-${i}`}>
                        <div className="flex items-start gap-2">
                          <SevIcon size={14} className={`${sev.color} flex-shrink-0 mt-0.5`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${sev.color} ${sev.bg}`}>
                                {sev.label}
                              </span>
                              <span className="text-[9px] text-[var(--text-muted)] flex items-center gap-1">
                                <CatIcon size={9} />{issue.category}
                              </span>
                              {issue.shots_affected?.length > 0 && (
                                <span className="text-[9px] text-[var(--text-disabled)]">
                                  Shots: {issue.shots_affected.join(", ")}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[var(--text-primary)]">{issue.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Strengths */}
            {report.strengths?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                  <CheckCircle2 size={13} className="text-green-400" />
                  Strengths
                </h4>
                <div className="space-y-1.5">
                  {report.strengths.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-green-500/5 border border-green-500/20">
                      <CheckCircle2 size={12} className="text-green-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-[var(--text-primary)]">{s}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No issues */}
            {report.issues?.length === 0 && (
              <div className="text-center py-8 bg-green-500/5 rounded-xl border border-green-500/20">
                <CheckCircle2 size={28} className="mx-auto text-green-400 mb-2" />
                <p className="text-sm font-medium text-green-400">Perfect Continuity</p>
                <p className="text-xs text-[var(--text-muted)] mt-1">No issues found across all shots</p>
              </div>
            )}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
